import { createClient } from 'contentful';

// Contentful client
// Using direct API keys since environment variables aren't available in Lovable
const client = createClient({
  // Replace these placeholder values with your actual Contentful credentials
  space: '0yx4uhftxlpl', // Your actual Space ID from Contentful
  accessToken: '9PxExAHcXwdygvRZ_mSN0R61skyUjmfOa0FcbiWAV-g', // Your actual Content Delivery API key
});

// Types for our content models
export interface WorkItem {
  id?: string;
  title: string;
  videoUrl: string;
  videoUrl2?: string;
  videoUrl3?: string;
  posterImage?: string;
  aspectRatio: "portrait" | "landscape" | "square";
  categories: string[];
  client?: string;
  editSoftware?: string;
  colorGrade?: string;
  additionalInfo?: string;
}

export interface Category {
  id: string;
  label: string;
}

// About page content interface
export interface AboutContent {
  photo: string;
  bio: string[];
  skills: string[];
  languages: string[];
}

// Helper function to check if we're using valid credentials
const isUsingPlaceholderCredentials = () => {
  return (
    client.getSpace === undefined || 
    client.getContentType === undefined
  );
};

// Map Contentful work entry to WorkItem
const mapWorkEntry = (item: any): WorkItem => {
  const fields = item.fields;
  return {
    id: item.sys.id,
    title: fields.title || '',
    videoUrl: fields.videoUrl || '',
    videoUrl2: fields.videoUrl2 || '',
    videoUrl3: fields.videoUrl3 || '',
    posterImage: fields.posterImage?.fields?.file?.url,
    aspectRatio: fields.aspectRatio || 'landscape',
    categories: fields.categories?.map((cat: any) => cat.fields?.slug || '') || [],
    client: fields.client,
    editSoftware: fields.editSoftware,
    colorGrade: fields.colorGrade,
    additionalInfo: fields.additionalInfo,
  };
};

// Fetch all works
export const getWorks = async (): Promise<WorkItem[]> => {
  try {
    // Check if we're using placeholder credentials
    if (isUsingPlaceholderCredentials()) {
      console.log('Contentful credentials not configured. Returning mock data.');
      return getMockWorks();
    }
    
    const entries = await client.getEntries({
      content_type: 'work',
      include: 2,
    });

    return entries.items.map(mapWorkEntry);
  } catch (error) {
    console.error('Error fetching works from Contentful:', error);
    return getMockWorks();
  }
};

// Fetch all categories
export const getCategories = async (): Promise<Category[]> => {
  try {
    // Check if we're using placeholder credentials
    if (isUsingPlaceholderCredentials()) {
      console.log('Contentful credentials not configured. Returning mock data.');
      return getMockCategories();
    }
    
    const entries = await client.getEntries({
      content_type: 'category',
    });

    return entries.items.map((item: any) => {
      const fields = item.fields;
      return {
        id: fields.slug,
        label: fields.name,
      };
    });
  } catch (error) {
    console.error('Error fetching categories from Contentful:', error);
    return getMockCategories();
  }
};

// Fetch featured works for the homepage reels
export const getFeaturedWorks = async (): Promise<{ [key: string]: WorkItem }> => {
  try {
    // Check if we're using placeholder credentials
    if (isUsingPlaceholderCredentials()) {
      console.log('Contentful credentials not configured. Returning mock data.');
      return getMockFeaturedWorks();
    }
    
    const entries = await client.getEntries({
      content_type: 'featuredWork',
      include: 2,
    });

    const featuredWorks: { [key: string]: WorkItem } = {};
    
    entries.items.forEach((item: any) => {
      const fields = item.fields;
      const workFields = fields.work?.fields;
      
      if (workFields) {
        featuredWorks[fields.reelType] = {
          title: workFields.title,
          videoUrl: workFields.videoUrl,
          videoUrl2: workFields.videoUrl2,
          videoUrl3: workFields.videoUrl3,
          posterImage: workFields.posterImage?.fields?.file?.url,
          aspectRatio: workFields.aspectRatio || 'landscape',
          categories: workFields.categories?.map((cat: any) => cat.fields.slug) || [],
          client: workFields.client,
          editSoftware: workFields.editSoftware,
          colorGrade: workFields.colorGrade,
          additionalInfo: workFields.additionalInfo,
        };
      }
    });

    return featuredWorks;
  } catch (error) {
    console.error('Error fetching featured works from Contentful:', error);
    return getMockFeaturedWorks();
  }
};

// Fetch works with featured category
export const getFeaturedCategoryWorks = async (): Promise<WorkItem[]> => {
  try {
    if (isUsingPlaceholderCredentials()) {
      return getMockWorks().filter(work => work.categories.includes('featured'));
    }
    
    // First, try to find the featured category
    const categories = await getCategories();
    const featuredCategory = categories.find(cat => cat.id === 'featured');
    
    if (!featuredCategory) {
      console.warn('Featured category not found');
      return [];
    }
    
    // Then fetch works that reference this category
    const entries = await client.getEntries({
      content_type: 'work',
      'fields.categories.sys.id': featuredCategory.id,
      include: 2,
    });
    
    return entries.items.map(mapWorkEntry);
  } catch (error) {
    console.error('Error fetching featured works:', error);
    return [];
  }
};

// Fetch About page content
export const getAboutContent = async (): Promise<AboutContent> => {
  try {
    if (isUsingPlaceholderCredentials()) {
      console.log('Contentful credentials not configured. Returning mock about data.');
      return getMockAboutContent();
    }
    
    const entries = await client.getEntries({
      content_type: 'about',
      limit: 1,
    });

    if (entries.items.length === 0) {
      console.warn('No about content found in Contentful');
      return getMockAboutContent();
    }

    const aboutData = entries.items[0].fields as any;
    
    // Format the bio content to properly handle Contentful's rich text
    let bioContent: string[] = [];
    
    if (aboutData.bio) {
      if (typeof aboutData.bio === 'string') {
        // If it's a single string, use it directly
        bioContent = [aboutData.bio];
      } else if (Array.isArray(aboutData.bio)) {
        // If it's an array of objects (rich text), convert to markdown
        bioContent = aboutData.bio.map((item: any) => {
          if (typeof item === 'string') {
            return item;
          } else if (item && item.content) {
            return JSON.stringify(item); // This will be parsed by the rich text renderer
          }
          return String(item || '');
        });
      } else if (aboutData.bio.content) {
        // If it's a rich text document object
        bioContent = [String(aboutData.bio)];
      }
    }
    
    return {
      photo: aboutData.photo?.fields?.file?.url || '',
      bio: bioContent.length > 0 ? bioContent : ["No bio available"],
      skills: Array.isArray(aboutData.skills) 
        ? aboutData.skills.map((item: any) => String(item)) 
        : [],
      languages: Array.isArray(aboutData.languages) 
        ? aboutData.languages.map((item: any) => String(item)) 
        : [],
    };
  } catch (error) {
    console.error('Error fetching about content from Contentful:', error);
    return getMockAboutContent();
  }
};

// Mock data functions to use when Contentful is not properly configured
const getMockWorks = (): WorkItem[] => {
  return [
    {
      id: 'mock-1',
      title: "Cinematic Commercial",
      videoUrl: "https://example.com/videos/commercial.mp4",
      videoUrl2: "https://example.com/videos/commercial-part2.mp4",
      videoUrl3: "https://example.com/videos/commercial-part3.mp4",
      posterImage: "https://example.com/images/commercial-poster.jpg",
      aspectRatio: "landscape",
      categories: ["featured", "commercial"],
      client: "Example Brand",
      editSoftware: "Premiere Pro",
      colorGrade: "DaVinci Resolve",
    },
    {
      id: 'mock-2',
      title: "Music Video",
      videoUrl: "https://example.com/videos/music.mp4",
      videoUrl2: "https://example.com/videos/music-part2.mp4",
      videoUrl3: "https://example.com/videos/music-part3.mp4",
      posterImage: "https://example.com/images/music-poster.jpg",
      aspectRatio: "landscape",
      categories: ["featured", "music-video"],
      client: "Artist Name",
      editSoftware: "Final Cut Pro",
      colorGrade: "DaVinci Resolve",
    },
    {
      id: 'mock-3',
      title: "Documentary Short",
      videoUrl: "https://example.com/videos/documentary.mp4",
      videoUrl2: "https://example.com/videos/documentary-part2.mp4",
      videoUrl3: "https://example.com/videos/documentary-part3.mp4",
      posterImage: "https://example.com/images/documentary-poster.jpg",
      aspectRatio: "landscape",
      categories: ["documentary"],
      client: "Documentary Foundation",
      editSoftware: "Premiere Pro",
      colorGrade: "Lumetri Color",
    }
  ];
};

const getMockCategories = (): Category[] => {
  return [
    { id: "featured", label: "Featured" },
    { id: "commercial", label: "Commercial" },
    { id: "music-video", label: "Music Video" },
    { id: "documentary", label: "Documentary" }
  ];
};

const getMockFeaturedWorks = (): { [key: string]: WorkItem } => {
  return {
    videoEditReel: {
      title: "Video Edit Reel",
      videoUrl: "/videos/editorial-reel.mp4",
      videoUrl2: "/videos/editorial-reel-part2.mp4",
      videoUrl3: "/videos/editorial-reel-part3.mp4",
      aspectRatio: "landscape",
      categories: ["featured", "editorial"],
      client: "Various",
      editSoftware: "Premiere Pro",
      colorGrade: "DaVinci Resolve",
    },
    motionReel: {
      title: "Motion Reel",
      videoUrl: "/videos/commercial-reel.mp4",
      videoUrl2: "/videos/commercial-reel-part2.mp4",
      videoUrl3: "/videos/commercial-reel-part3.mp4",
      aspectRatio: "landscape",
      categories: ["featured", "commercial"],
      client: "Various",
      editSoftware: "Final Cut Pro",
      colorGrade: "DaVinci Resolve",
    }
  };
};

// Mock data for About page
const getMockAboutContent = (): AboutContent => {
  return {
    photo: "https://images.unsplash.com/photo-1496345875659-11f7dd282d1d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2300&q=80",
    bio: [
      "I'm **Yuri Lobo**, a passionate video editor and motion designer with over 10 years of experience crafting compelling visual stories for brands, individuals, and creative projects.",
      "My work spans across various domains - from corporate videos and documentaries to social media content and wedding films. I specialize in creating engaging content that connects with audiences and effectively communicates my clients' visions.",
      "With expertise in **Adobe Premiere Pro**, **After Effects**, and **DaVinci Resolve**, I bring technical skill alongside a keen artistic eye to every project I undertake."
    ],
    skills: [
      "Video Editing", "Motion Graphics", "Color Grading", "Storytelling", 
      "Sound Design", "Visual Effects", "Photoshop Wizardry"
    ],
    languages: ["Portuguese", "English", "Spanish"]
  };
};
